import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, ArrowRight, Loader2, ShieldCheck, Zap, Star } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/api';

function LoginPage() {
  const [loginCredential, setLoginCredential] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiClient.post('/auth/login', { login: loginCredential, password });
      if (response.data && response.data.userId) {
        login(response.data, navigate);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex font-sans overflow-hidden">
      
      {/* Left Side: Visual/Branding (Hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-40 mix-blend-luminosity"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-emerald-900/90 via-slate-900/80 to-transparent"></div>
        
        <div className="relative z-10 max-w-lg">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center text-white shadow-2xl rotate-3">
                <Zap size={32} fill="currentColor" />
            </div>
            
            <div>
                <h1 className="text-6xl font-black text-white tracking-tighter leading-[1.1]">
                    Powering the <br/> 
                    <span className="text-emerald-400 italic font-serif">Future of Labor.</span>
                </h1>
                <p className="text-slate-300 mt-6 text-lg font-medium leading-relaxed">
                    Connecting skilled workers with farmers and buyers across Bangladesh. Simple, secure, and transparent.
                </p>
            </div>

            <div className="grid grid-cols-2 gap-6 pt-8">
                <div className="bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/10">
                    <ShieldCheck className="text-emerald-400 mb-2" size={24} />
                    <p className="text-white font-bold text-sm">Verified Profiles</p>
                    <p className="text-slate-400 text-xs mt-1">Trusted community of professionals.</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/10">
                    <Star className="text-yellow-400 mb-2" size={24} />
                    <p className="text-white font-bold text-sm">Escrow Payments</p>
                    <p className="text-slate-400 text-xs mt-1">Your money is safe with us.</p>
                </div>
            </div>
          </motion.div>
        </div>

        {/* Abstract shapes */}
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl animate-pulse"></div>
      </div>

      {/* Right Side: Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12 lg:p-20 bg-slate-50/50">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo for mobile */}
          <div className="lg:hidden flex items-center justify-center mb-12">
            <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center text-white font-black text-2xl shadow-lg mr-3">S</div>
            <span className="text-3xl font-black text-slate-800 tracking-tighter italic">Service<span className="text-emerald-600">24</span></span>
          </div>

          <div className="mb-10">
            <h2 className="text-4xl font-black text-slate-900 tracking-tight mb-2">Welcome Back.</h2>
            <p className="text-slate-500 font-medium">Log in to manage your work and earnings.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email or Phone</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-emerald-600 transition-colors">
                    <Mail size={18} />
                  </div>
                  <input 
                    type="text" 
                    placeholder="Enter credentials..." 
                    value={loginCredential} 
                    onChange={(e) => setLoginCredential(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-2xl text-slate-900 font-bold placeholder-slate-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all shadow-sm" 
                    required 
                  />
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex justify-between items-center px-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Password</label>
                    <Link to="/forgot-password" size={10} className="text-[10px] font-black text-emerald-600 hover:text-emerald-700 uppercase tracking-widest">Forgot?</Link>
                </div>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-emerald-600 transition-colors">
                    <Lock size={18} />
                  </div>
                  <input 
                    type="password" 
                    placeholder="••••••••" 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-2xl text-slate-900 font-bold placeholder-slate-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all shadow-sm" 
                    required 
                  />
                </div>
              </div>
            </div>

            {error && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-4 rounded-2xl bg-red-50 border border-red-100 text-red-600 text-xs font-black text-center uppercase tracking-wider"
              >
                {error}
              </motion.div>
            )}

            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-slate-900 hover:bg-black text-white font-black py-5 px-4 rounded-2xl shadow-2xl shadow-slate-200 transition-all duration-300 transform active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-3 group relative overflow-hidden"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <span className="uppercase tracking-widest text-sm">Sign In to Dashboard</span>
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>

            <div className="pt-8 text-center border-t border-slate-100">
              <p className="text-slate-400 font-medium">
                New to the platform?{' '}
                <Link to="/register" className="text-slate-900 hover:text-emerald-600 font-black transition-colors underline decoration-emerald-500/30 underline-offset-4">
                  Create an account
                </Link>
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}

export default LoginPage;
